import { apiFetch } from './apiClient';

/**
 * Mirror of engine/shared/resolve-rules.ts and backend/src/library/price-rules.ts — the same
 * sane-price window decides which competitor price counts as a match.
 */
export const PRICE_SANE_MIN = 0.6;
export const PRICE_SANE_MAX = 1.6;

export const REASON_LABELS: Record<string, string> = {
  wrong_domain: 'Wrong domain',
  not_product_page: 'Not a product page',
  title_mismatch: 'Title mismatch',
  price_out_of_range: 'Price out of range',
  loose_selector: 'Loose selector',
  low_confidence: 'Low confidence',
  not_sold: 'Not sold',
  failed: 'Failed',
};

export interface ReviewSibling {
  id: number;
  url: string | null;
  status: string;
}

export interface ReviewRow {
  id: number;
  prod_id: number;
  title: string;
  url: string;
  selector: string | null;
  stock_selector: string | null;
  dt: string;
  prod_title: string | null;
  brand: string | null;
  sku: string | null;
  category: string | null;
  our_price: number | null;
  comp_price: number | null;
  comp_stock: number | null;
  last_checked: string | null;
  reason: string | null;
  reason_detail: string | null;
  reason_price: number | null;
  anchor_price: number | null;
  confidence: number | null;
  ratio: number | null;
  ratio_source: 'review' | 'log';
  siblings: ReviewSibling[];
}

export interface ReviewFilters {
  page?: number;
  limit?: number;
  domain?: string;
  category?: string;
  reason?: string;
  band?: 'low' | 'in' | 'high' | 'unknown';
  q?: string;
}

export interface Unscrapeable {
  id: number;
  reason: 'no_url' | 'no_selector';
}

export interface ReviewApproveResult {
  approved: number;
  unscrapeable: Unscrapeable[];
}

export interface ReviewRejectResult {
  rejected: number;
}

export interface ReviewUpdateResult {
  updated: number;
  status: string;
  unscrapeable: Unscrapeable[];
}

export const reviewService = (token: string | null) => ({
  list: (filters: ReviewFilters) => {
    const params = new URLSearchParams();
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.domain) params.set('domain', filters.domain);
    if (filters.category) params.set('category', filters.category);
    if (filters.reason) params.set('reason', filters.reason);
    if (filters.band) params.set('band', filters.band);
    if (filters.q) params.set('q', filters.q);
    return apiFetch<{ rows: ReviewRow[]; total: number; page: number; limit: number }>(
      token,
      `/api/review?${params.toString()}`
    );
  },
  /** Every id matching the filters - the select-all across pages needs the whole set, not one page. */
  ids: (filters: ReviewFilters) => {
    const params = new URLSearchParams();
    if (filters.domain) params.set('domain', filters.domain);
    if (filters.category) params.set('category', filters.category);
    if (filters.reason) params.set('reason', filters.reason);
    if (filters.band) params.set('band', filters.band);
    if (filters.q) params.set('q', filters.q);
    return apiFetch<{ ids: number[] }>(token, `/api/review/ids?${params.toString()}`);
  },
  count: () => apiFetch<{ count: number }>(token, '/api/review/count'),
  approve: (ids: number[]) =>
    apiFetch<ReviewApproveResult>(token, '/api/review/approve', { method: 'POST', body: JSON.stringify({ ids }) }),
  reject: (ids: number[]) =>
    apiFetch<ReviewRejectResult>(token, '/api/review/reject', { method: 'POST', body: JSON.stringify({ ids }) }),
  update: (id: number, payload: { url?: string; selector?: string; stock_selector?: string }) =>
    apiFetch<ReviewUpdateResult>(token, `/api/review/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
});
